import React, { createContext, useContext, useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { API_BASE, getToken } from '../utils/api';

const AuthContext = createContext();

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [customer, setCustomer] = useState(null);

  useEffect(() => {
    const token = getToken();
    if (!token) {
      setIsLoading(false);
      return;
    }
    fetch(`${API_BASE}/customer/auth/profile`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.success) {
          setCustomer(data.data);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('customerToken');
        }
      })
      .catch(() => localStorage.removeItem('customerToken'))
      .finally(() => setIsLoading(false));
  }, []);

  const setSession = (token, customerData) => {
    localStorage.setItem('customerToken', token);
    setCustomer(customerData);
    setIsAuthenticated(true);
  };

  const login = async (email, password) => {
    const res = await fetch(`${API_BASE}/customer/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });
    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.message || 'Login failed');
      err.code = data.code;
      throw err;
    }
    setSession(data.data.token, data.data.customer);
    toast.success('Welcome back');
    return data;
  };

  const logout = async () => {
    const token = getToken();
    if (token) {
      try {
        await fetch(`${API_BASE}/customer/auth/logout`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
        });
      } catch (_) {
        /* ignore */
      }
    }
    localStorage.removeItem('customerToken');
    setCustomer(null);
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider
      value={{ isAuthenticated, isLoading, customer, setSession, login, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
};
